import { fetchReservations, manageCancelReservation, manageConfirmReservation, manageCreateReservation, manageDeleteReservation, manageGetOccupiedCapacity, manageUpdateReservation, manageValidateReservation } from '@/lib/services/reservation'
import { Reservation, ReservationStatus } from '@/lib/types/reservation'
import { Resource } from '@/lib/types/resource'
import { ResourceType } from '@/lib/types/resourceType'
import { VisitorType } from '@/lib/types/visitor'
import { defineStore } from 'pinia'

interface ReservationState {
  reservations: Reservation[]
  loading: boolean
}

export const useReservationsStore = () => {
  const inner = defineStore('reservations', {
    state: (): ReservationState => ({
      reservations: [],
      loading: false
    }),
    actions: {
      // `from`/`to` are `yyyy-MM-dd` calendar-date strings, not instants — see fetchReservations in
      // lib/services/reservation.ts. The caller owns the clock the calendar date is read on.
      async fetchByDates(from: string, to: string, reservationType?: 'INTERNAL' | 'EXTERNAL', resourceTypes?: ResourceType[], resources?: Resource[], visitorTypes?: VisitorType[], own?: boolean): Promise<PagedResultMetadata> {
        this.loading = true

        try {
          const res = await fetchReservations(from, to, reservationType, resourceTypes, resources, visitorTypes, own)
          const { items, ...metadata } = res
          this.reservations = items

          return metadata as PagedResultMetadata
        } catch (err) {
          console.error(err)
          throw err
        } finally {
          this.loading = false
        }
      },

      async createOne(reservation: Reservation): Promise<ErrorMessage | void> {
        const created = await manageCreateReservation(reservation)

        if (created && 'errorCode' in created) {
          return created
        }

        this.reservations.push(created)
      },

      async updateOne(reservation: Reservation): Promise<ErrorMessage | void> {
        const updated = await manageUpdateReservation(reservation)

        if (updated && 'errorCode' in updated) {
          return updated
        }

        const index = this.reservations.findIndex(r => r.id === updated.id)
        if (index !== -1) this.reservations[index] = updated
      },

      async confirmOne(reservation: Reservation): Promise<ErrorMessage | void> {
        const confirmed = await manageConfirmReservation(reservation.id)

        if (confirmed && 'errorCode' in confirmed) {
          return confirmed
        }

        const index = this.reservations.findIndex(r => r.id === confirmed.id)
        if (index !== -1) this.reservations[index] = confirmed
      },

      async validateOne(reservation: Reservation): Promise<ErrorMessage | void> {
        return await manageValidateReservation(reservation);
      },

      async deleteOne(id: string): Promise<ErrorMessage | void> {
        const result = await manageDeleteReservation(id)

        if (result) {
          return result
        }

        this.reservations = this.reservations.filter(r => r.id !== id)
      },

      async cancelOne(id: string, reason?: string): Promise<ErrorMessage | void> {
        const result = await manageCancelReservation(id, reason)

        if (result) {
          return result
        }

        const reservation = this.reservations.find(r => r.id === id)
        if (reservation) {
          reservation.status = ReservationStatus.Canceled
        }
      },

      async getOccupiedCapacityForResource(resource: Resource, from: Date, to: Date, excludeReservationId: string | null): Promise<{ total: number, available: number }> {
        try {
          return await manageGetOccupiedCapacity(resource, from, to, excludeReservationId)
        } catch (err) {
          console.error(err)
          throw err
        }
      },
    },
  })

  const store = inner();

  return store;
}
